import React, { createContext, useState, useEffect, useContext } from 'react';
import { 
  getCurrentUser, 
  login as authLogin, 
  logout as authLogout, 
  register as authRegister,
  updateDetails as authUpdateDetails,
  updatePassword as authUpdatePassword,
  isAuthenticated as checkAuth,
  loginWithGoogle as authLoginWithGoogle
} from '../services/authService';

// Create the context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on initial load
  useEffect(() => {
    const loadUser = async () => {
      try {
        if (checkAuth()) {
          const userData = await getCurrentUser();
          setUser(userData);
        }
      } catch (err) {
        console.error('Failed to load user', err);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Register a new user
  const register = async (userData) => {
    try {
      setError(null);
      const response = await authRegister(userData);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Registration failed');
      throw err;
    }
  };

  // Login user
  const login = async (email, password, keepMeLoggedIn = true) => {
    try {
      setError(null);
      const response = await authLogin(email, password);
      const token = response?.token;

      if (token) {
        if (keepMeLoggedIn) {
          localStorage.setItem('token', token);
          sessionStorage.removeItem('token');
        } else {
          sessionStorage.setItem('token', token);
          localStorage.removeItem('token');
        }
      }

      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    }
  };

  // Login with Google
  const loginWithGoogle = async (credential) => {
    try {
      setError(null);
      const response = await authLoginWithGoogle(credential);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Google login failed');
      throw err;
    }
  };

  // Logout user
  const logout = async () => {
    try {
      await authLogout();
      setUser(null);
    } catch (err) {
      console.error('Logout failed', err);
      setError(err.message || 'Logout failed');
      throw err;
    }
  };

  // Update user details
  const updateDetails = async (userData) => {
    try {
      setError(null);
      const updatedUser = await authUpdateDetails(userData);
      setUser(prevUser => ({
        ...prevUser,
        ...updatedUser,
      }));
      return updatedUser;
    } catch (err) {
      setError(err.message || 'Failed to update details');
      throw err;
    }
  };

  // Update password
  const updatePassword = async (currentPassword, newPassword) => {
    try {
      setError(null);
      return await authUpdatePassword(currentPassword, newPassword);
    } catch (err) {
      setError(err.message || 'Failed to update password');
      throw err;
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isAuthenticated,
        hasRole,
        register,
        login,
        loginWithGoogle,
        logout,
        updateDetails,
        updatePassword,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { useAuth };
export default AuthContext;
